/**
 * teamViewStore — the lane in view, per lead task (spec 2026-09-06 §3.2, D2).
 *
 * The selection IS the recipient IS the screen: picking a chip on the bench
 * hands that lane the column, points the composer at it, and is the only way
 * back to Bash. One piece of state carries all three, so they can never
 * disagree — which is what the sheet, the To: pill and the rail's Team list
 * each kept their own answer to before.
 *
 * Keyed by the LEAD task, so a lane picked on task A can never paint over task
 * B. Not persisted and not reset on delete: a stale key names a lane that is no
 * longer on the roster, and the bench sends the screen back to Bash the moment
 * it sees one (the store never reads the task store).
 */

import { create } from "zustand";

interface TeamViewStore {
  /** The lane in view, per lead task. Absent = Bash. */
  byTask: Record<string, string | undefined>;
  select: (taskId: string, teammateTaskId: string | null) => void;
  reset: (taskId: string) => void;
}

/** Stable across every empty state — a fresh `{}` would re-render every reader. */
const NO_SELECTION: Record<string, string | undefined> = {};

/** Drop the key rather than storing `undefined`: `'t' in byTask` then answers
 *  the same question `byTask.t` does, and the record never grows a tail of
 *  tasks nobody is looking at. */
function without(
  byTask: Record<string, string | undefined>,
  taskId: string
): Record<string, string | undefined> {
  if (!(taskId in byTask)) return byTask;
  const next = { ...byTask };
  delete next[taskId];
  return next;
}

export const useTeamViewStore = create<TeamViewStore>((set) => ({
  byTask: NO_SELECTION,
  select: (taskId, teammateTaskId) =>
    set((s) => {
      if (teammateTaskId === null) return { byTask: without(s.byTask, taskId) };
      if (s.byTask[taskId] === teammateTaskId) return s;
      return { byTask: { ...s.byTask, [taskId]: teammateTaskId } };
    }),
  reset: (taskId) => set((s) => ({ byTask: without(s.byTask, taskId) })),
}));

/** The lane this task is showing, or null for Bash. */
export function useLaneInView(
  taskId: string | null | undefined
): string | null {
  return useTeamViewStore((s) => (taskId ? (s.byTask[taskId] ?? null) : null));
}
